import {
  Button,
  Card,
  Col,
  Descriptions,
  Input,
  List,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import axios from 'axios';
import { useAuthenticatedRequest, UserContext } from '../../UserContext';
import {
  AudioOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExportOutlined,
  InfoCircleOutlined,
  PhoneOutlined,
  ReloadOutlined,
  SoundFilled,
  SoundOutlined,
} from '@ant-design/icons';
import { ClientNavigation } from '../../components/client/client-navigation';
import { LeadConversation } from './lead-conversation';
import {
  eventColor,
  LeadEngagementLedger,
} from '../../Insights/LeadEngagementLedger';
import { LeadFacts } from '../../Insights/LeadFacts';
import { EngagementSummary } from './engagement-summary';
import { CadenceTimeline } from './cadence-timeline';
import { EngagementTimeline } from './engagement-timeline';
import { EngagementOverview } from './engagement-overview';
import { Agent, ClientConfig, CrmType } from '../../types';
import { getAgentTimezone, getCrmLink } from '../agentSms/utils';
import { formatDateWithZone } from './format-date';
import { sanitizeHTML } from '../../utils';
import { isOverdue, relativeFromNow } from './relative-time';
import { getLeadEngagementHashTarget } from './lead-engagement-navigation';

const { Title } = Typography;

const STAGE_COLORS: Record<string, string> = {
  new: 'blue',
  engaged: 'cyan',
  nurture: 'geekblue',
  warm: 'gold',
  hot: 'volcano',
  cold: 'default',
};

const CHANNEL_LABELS: Record<string, string> = {
  sms: 'SMS',
  phone_call: 'call',
  sms_and_phone_call: 'SMS + call',
  none: 'no outreach',
};

export const reengageLead = (
  apiUrl: string,
  leadId: string,
  headers: Record<string, string> = {},
) =>
  axios.post(
    `${apiUrl}/api/lead-engagement/leads/${leadId}/re-engage`,
    undefined,
    { headers },
  );

// Ledger current-state values (LeadEngagementEventType) for the leads-table filter. Single-select:
// the backend `state` param filters on one current state at a time.
const LEDGER_STATE_FILTERS = [
  'eligible',
  'ineligible',
  'blocked',
  'disqualified',
  'transferred',
  'met',
  'transacted',
  'deactivated',
].map(state => ({ text: state, value: state }));

// Campaign filter options come from the campaign_keys seen in loaded pages (there is no
// campaigns list endpoint), merged with previously seen keys so the options survive filtering.
export const mergeCampaignKeys = (
  known: string[],
  engagements: Array<{ campaign_key?: string | null }>,
): string[] => {
  const keys = new Set(known);
  engagements.forEach(engagement => {
    if (engagement.campaign_key) {
      keys.add(engagement.campaign_key);
    }
  });
  return keys.size === known.length ? known : Array.from(keys).sort();
};

/** Core system health metrics (HOU-2805). Backend: GET /api/lead-engagements/:clientId/system-health */
interface SystemHealthMetrics {
  leads_engaged: number;
  engagement_rate: number; // Percent of leads that have responded to the engagement.
  agent_connection_rate: number; // % of calls where agent picked up
  leads_connected_rate: number; // % of leads connected to an agent
  new_leads_engaged: number; // Amount of new leads that have been reached out throughout the past week.
}

export interface LeadEngagement {
  id: string;
  lead_id: string;
  phone_number: string;
  is_active: boolean;
  created_at: string;
  lead?: {
    id: string;
    source_id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    is_in_pond?: boolean | null;
    pond_id?: string | null;
    pond_name?: string | null;
    source?: string | null;
    stage?: string | null;
    tags?: string[] | null;
    agent?: {
      id: string;
      first_name: string;
      last_name: string;
      timezone?: string;
      phone: string;
    };
    last_communication?: {
      id: string;
      created_at: string;
      body?: string;
    };
  };
  action_plan_name?: string;
  next_evaluation_at?: string;
  lead_type?: string;
  message_count?: number;
  ai_message_count?: number;
  is_connected?: boolean;
  is_three_way?: boolean;
  transfer_date?: string | null;
  ledger_state?: string | null;
  // Set only on rejected rows, which come from the ledger and have no lead engagement.
  lead_engagement_id?: null;
  ledger_reason?: string | null;
  campaign_key?: string | null;
  trigger_type?: string | null;
  stage?: string | null;
  outreach_channel?: string | null;
  preferred_language?: string | null;
  engagement_zip_code?: string | null;
}

export const LeadEngagementsPage = ({ apiUrl }: { apiUrl: string }) => {
  const savedUrl = sessionStorage.getItem('preAuthUrl');
  const savedParams = savedUrl ? new URL(savedUrl) : null;
  const savedLeadId = useRef(savedParams?.searchParams.get('leadId') || null);
  const savedHash = useRef(savedParams?.hash || window.location.hash || '');
  const { messageId: initialMessageId, tab: initialEngagementTab } =
    getLeadEngagementHashTarget(savedHash.current);

  useEffect(() => {
    if (
      !window.location.hash &&
      savedHash.current &&
      savedHash.current.startsWith('#message-')
    ) {
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${window.location.search}${savedHash.current}`,
      );
    }
    sessionStorage.removeItem('preAuthUrl');
  }, []);
  const { clientId: clientIdParam } = useParams<{ clientId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = clientIdParam || searchParams.get('clientId') || '';
  const leadIdFromUrl = searchParams.get('leadId') || savedLeadId.current;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [engagements, setEngagements] = useState<LeadEngagement[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEngagement, setSelectedEngagement] =
    useState<LeadEngagement | null>(null);
  const [isInfoModalVisible, setIsInfoModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('timeline');
  const [notes, setNotes] = useState<any[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [selectedNote, setSelectedNote] = useState<any | null>(null);
  const [isNoteDetailModalVisible, setIsNoteDetailModalVisible] =
    useState(false);
  const [filters, setFilters] = useState<any>({});
  // A rejected row has no engagement to open, so its popup shows the lead and its ledger instead.
  const [rejectedLead, setRejectedLead] = useState<LeadEngagement | null>(null);
  const [campaignKeys, setCampaignKeys] = useState<string[]>([]);
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<string>('desc');
  const [calls, setCalls] = useState<any[]>([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [routingData, setRoutingData] = useState<any[]>([]);
  const [routingLoading, setRoutingLoading] = useState(false);
  const [toggleActiveLoading, setToggleActiveLoading] = useState(false);
  const [audioUrls, setAudioUrls] = useState<Record<string, string>>({});
  // Lazy-loaded per-agent-attempt recording blobs, keyed by attempt.id.
  const [attemptAudioUrls, setAttemptAudioUrls] = useState<
    Record<string, string>
  >({});
  const [attemptAudioLoading, setAttemptAudioLoading] = useState<
    Record<string, boolean>
  >({});
  const [attemptAudioError, setAttemptAudioError] = useState<
    Record<string, string>
  >({});
  const [clientConfig, setClientConfig] = useState<ClientConfig | null>(null);
  const getFullAgent = (agent?: any) => {
    if (!agent) {
      return undefined;
    }
    return clientConfig?.agents?.find(a => a.id === agent.id) || agent;
  };

  const selectedAgent = selectedEngagement
    ? getFullAgent(selectedEngagement.lead?.agent)
    : null;
  // getAgentTimezone already falls back client-config -> default; '' reaches toLocaleString and throws.
  const engagementTimezone = getAgentTimezone(
    selectedAgent ?? undefined,
    clientConfig,
  );
  const [pageTab, setPageTab] = useState<string>('engagements');
  const [systemHealthMetrics, setSystemHealthMetrics] =
    useState<SystemHealthMetrics | null>(null);
  const [systemHealthLoading, setSystemHealthLoading] = useState(false);
  const [systemHealthUpdatedAt, setSystemHealthUpdatedAt] =
    useState<Date | null>(null);
  const user = useContext(UserContext);
  const authenticatedRequest = useAuthenticatedRequest(user?.accessToken);

  const fetchEngagements = async (
    page: number,
    size: number,
    search: string,
    tableFilters: any = {},
    sort_field: string = 'created_at',
    sort_order: string = 'desc',
  ) => {
    if (!clientId) {
      return;
    }
    setLoading(true);
    try {
      const params: any = {
        page,
        page_size: size,
        search,
      };

      // Add filters to params
      if (tableFilters.agent) {
        params.agent = tableFilters.agent.join(',');
      }
      if (tableFilters.lead_type) {
        params.lead_type = tableFilters.lead_type.join(',');
      }
      if (tableFilters.action_plan_name) {
        params.action_plan_name = tableFilters.action_plan_name.join(',');
      }
      if (tableFilters.is_active !== undefined) {
        params.is_active = tableFilters.is_active.join(',');
      }
      if (tableFilters.is_connected !== undefined) {
        params.is_connected = tableFilters.is_connected.join(',');
      }
      if (tableFilters.is_three_way !== undefined) {
        params.is_three_way = tableFilters.is_three_way.join(',');
      }
      if (tableFilters.name) {
        params.name_filter = tableFilters.name[0];
      }
      if (tableFilters.lead_id) {
        params.lead_id = tableFilters.lead_id[0];
      }
      if (tableFilters.source_id) {
        params.source_id = tableFilters.source_id[0];
      }
      if (tableFilters.ledger_state) {
        params.state = tableFilters.ledger_state[0];
      } else if (tableFilters.rejected) {
        // Rejected leads have no engagement, only a ledger fact; the server lists them separately.
        params.rejected = 'true';
      } else {
        params.exclude_state = 'ineligible';
      }
      if (tableFilters.campaign_key) {
        params.campaign_key = tableFilters.campaign_key.join(',');
      }

      // Add sorting parameters
      if (sort_field) {
        params.sort_field = sort_field;
        params.sort_order = sort_order;
      }

      const response = await authenticatedRequest(headers =>
        axios.get(`${apiUrl}/api/lead-engagements/${clientId}`, {
          params,
          headers,
        }),
      );
      if (response.data && response.data.items) {
        setEngagements(response.data.items);
        setCampaignKeys(known => mergeCampaignKeys(known, response.data.items));
        setTotal(response.data.total || 0);
        setCurrentPage(page);
        setPageSize(size);
      } else {
        console.error('Unexpected response format:', response.data);
        setEngagements([]);
        setTotal(0);
      }
    } catch (error: any) {
      console.error('Failed to fetch lead engagements:', error);
      if (error.response) {
        console.error(
          'Error response:',
          error.response.status,
          error.response.data,
        );
      }
      setEngagements([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemHealth = async () => {
    if (!clientId) {
      return;
    }
    setSystemHealthLoading(true);
    try {
      const response = await authenticatedRequest(headers =>
        axios.get<SystemHealthMetrics>(
          `${apiUrl}/api/lead-engagements/${clientId}/system-health`,
          { headers },
        ),
      );
      setSystemHealthMetrics(response.data);
      setSystemHealthUpdatedAt(new Date());
    } catch {
      setSystemHealthMetrics(null);
      setSystemHealthUpdatedAt(new Date());
    } finally {
      setSystemHealthLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      void fetchEngagements(
        1,
        pageSize,
        searchTerm,
        filters,
        sortField,
        sortOrder,
      );

      // Fetch client config
      const fetchClientConfig = async () => {
        try {
          const response = await authenticatedRequest(headers =>
            axios.get(`${apiUrl}/api/configs/${clientId}`, {
              headers,
            }),
          );
          setClientConfig(response.data);
        } catch (error) {
          console.error('Failed to fetch client config:', error);
        }
      };
      void fetchClientConfig();
    }
  }, [clientId, apiUrl, user?.accessToken]);

  useEffect(() => {
    if (clientId && pageTab === 'metrics') {
      void fetchSystemHealth();
    }
  }, [clientId, pageTab, apiUrl, user?.accessToken]);

  // Fetch a single lead's engagement by lead_id filter (for shared links when lead is not on current page)
  const fetchEngagementByLeadId = async (
    leadId: string,
  ): Promise<LeadEngagement | null> => {
    if (!clientId) {
      return null;
    }
    try {
      const response = await authenticatedRequest(headers =>
        axios.get(`${apiUrl}/api/lead-engagements/${clientId}`, {
          params: {
            page: 1,
            page_size: 1,
            search: '',
            lead_id: leadId,
            sort_field: sortField,
            sort_order: sortOrder,
          },
          headers,
        }),
      );
      const items = response.data?.items;
      return items?.length > 0 ? items[0] : null;
    } catch {
      return null;
    }
  };

  // Open lead modal when URL has leadId (e.g. shared link)
  useEffect(() => {
    if (!leadIdFromUrl || !clientId) {
      return;
    }
    if (selectedEngagement?.lead_id === leadIdFromUrl && isInfoModalVisible) {
      return;
    }
    const inList = engagements.find(
      e => e.lead_id === leadIdFromUrl || e.lead?.id === leadIdFromUrl,
    );
    if (inList) {
      void showMoreInfo(inList, initialEngagementTab);
      return;
    }
    // Lead not on current page: fetch using lead_id filter
    let cancelled = false;
    void fetchEngagementByLeadId(leadIdFromUrl).then(engagement => {
      if (!cancelled && engagement) {
        void showMoreInfo(engagement, initialEngagementTab);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    leadIdFromUrl,
    clientId,
    engagements,
    selectedEngagement?.lead_id,
    isInfoModalVisible,
    initialEngagementTab,
  ]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    void fetchEngagements(1, pageSize, value, filters, sortField, sortOrder);
  };

  // Drives the header's connection-status dropdown, which sets a boolean filter outside the
  // table's own filter dropdowns. "Connected" and "3-Way" are mutually exclusive here: a lead
  // can only match one selection at a time (3-way handoffs already count as connected server-side).
  type ConnectionFilter = 'all' | 'is_connected' | 'is_three_way' | 'rejected';

  const connectionFilterValue: ConnectionFilter = filters.rejected?.[0]
    ? 'rejected'
    : filters.is_three_way?.[0]
      ? 'is_three_way'
      : filters.is_connected?.[0]
        ? 'is_connected'
        : 'all';

  const handleConnectionFilterChange = (value: ConnectionFilter) => {
    const newFilters = { ...filters };
    delete newFilters.is_connected;
    delete newFilters.is_three_way;
    delete newFilters.rejected;
    if (value !== 'all') {
      newFilters[value] = [true];
    }
    setFilters(newFilters);
    void fetchEngagements(
      1,
      pageSize,
      searchTerm,
      newFilters,
      sortField,
      sortOrder,
    );
  };

  const handleTableChange = (
    pagination: any,
    tableFilters: any,
    sorter: any,
  ) => {
    const newFilters: any = {};

    // Process filters
    Object.keys(tableFilters).forEach(key => {
      if (tableFilters[key]) {
        newFilters[key] = tableFilters[key];
      }
    });

    // Preserve the header's connection-status dropdown filter since it lives outside the
    // table's own filter dropdowns and so never appears in tableFilters.
    (['is_connected', 'is_three_way', 'rejected'] as const).forEach(key => {
      if (filters[key] && !tableFilters[key]) {
        newFilters[key] = filters[key];
      }
    });

    // Process sorting
    let newSortField = 'created_at';
    let newSortOrder = 'desc';

    if (sorter && sorter.field) {
      newSortField = sorter.field;
      newSortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
    } else if (sorter && sorter.columnKey) {
      newSortField = sorter.columnKey;
      newSortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
    }

    setFilters(newFilters);
    setSortField(newSortField);
    setSortOrder(newSortOrder);
    void fetchEngagements(
      pagination.current,
      pagination.pageSize,
      searchTerm,
      newFilters,
      newSortField,
      newSortOrder,
    );
  };

  const showMoreInfo = async (
    engagement: LeadEngagement,
    tab: string = 'timeline',
  ) => {
    // If opening a different engagement, clear previous data
    if (selectedEngagement?.id !== engagement.id) {
      setNotes([]);
      setCalls([]);
    }

    setSelectedEngagement(engagement);
    setActiveTab(tab);
    setIsInfoModalVisible(true);
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.set('leadId', engagement.lead_id);
      return p;
    });

    // Fetch notes if opening notes tab
    const lead = engagement.lead;
    if (tab === 'notes' && lead) {
      setNotesLoading(true);
      try {
        const response = await authenticatedRequest(headers =>
          axios.get(
            `${apiUrl}/api/agent/${lead.agent?.id || 'unknown'}/lead/${lead.source_id}/notes`,
            { headers },
          ),
        );
        if (Array.isArray(response.data)) {
          setNotes(response.data);
        } else {
          console.error('Unexpected notes response format:', response.data);
          setNotes([]);
        }
      } catch (error: any) {
        console.error('Failed to fetch notes:', error);
        if (error.response) {
          console.error(
            'Error response:',
            error.response.status,
            error.response.data,
          );
        }
        setNotes([]);
      } finally {
        setNotesLoading(false);
      }
    }

    // Fetch calls if opening calls tab
    if (tab === 'calls') {
      setCallsLoading(true);
      try {
        const response = await authenticatedRequest(headers =>
          axios.get(`${apiUrl}/api/lead-engagement/${engagement.id}/calls`, {
            headers,
          }),
        );
        setCalls(response.data);
      } catch (error) {
        console.error('Failed to fetch calls:', error);
        setCalls([]);
      } finally {
        setCallsLoading(false);
      }
    }
  };

  // Fetch audio with auth headers for calls that use the API endpoint
  useEffect(() => {
    if (!calls.length || !user?.accessToken) {
      return;
    }

    const callsNeedingAudio = calls.filter((call: any) => !audioUrls[call.id]);
    if (!callsNeedingAudio.length) {
      return;
    }

    const active = true;
    const newUrls: Record<string, string> = {};

    const fetchAll = async () => {
      await Promise.all(
        callsNeedingAudio.map(async (call: any) => {
          try {
            const response = await authenticatedRequest(headers =>
              fetch(`${apiUrl}/api/lead-engagement/call/${call.id}/audio`, {
                headers,
              }),
            );
            if (response.ok && active) {
              const blob = await response.blob();
              newUrls[call.id] = URL.createObjectURL(blob);
            }
          } catch (err) {
            console.error('Error fetching call audio:', err);
          }
        }),
      );
      if (active && Object.keys(newUrls).length > 0) {
        setAudioUrls(prev => ({ ...prev, ...newUrls }));
      }
    };

    void fetchAll();
  }, [calls, apiUrl, user?.accessToken]);

  const handleInfoModalCancel = () => {
    setIsInfoModalVisible(false);
    setSelectedEngagement(null);
    savedLeadId.current = null;
    savedHash.current = '';
    setNotes([]);
    setCalls([]);
    setRoutingData([]);
    setAudioUrls({});
    setAttemptAudioUrls({});
    setAttemptAudioLoading({});
    setAttemptAudioError({});
    Object.values(audioUrls).forEach(url => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });
    Object.values(attemptAudioUrls).forEach(url => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });
    setActiveTab('timeline');
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.delete('leadId');
      return p;
    });
  };

  const showNoteDetail = (note: any) => {
    setSelectedNote(note);
    setIsNoteDetailModalVisible(true);
  };

  const handleNoteDetailModalCancel = () => {
    setIsNoteDetailModalVisible(false);
    setSelectedNote(null);
  };

  const loadAttemptAudio = async (attemptId: string) => {
    if (attemptAudioUrls[attemptId] || attemptAudioLoading[attemptId]) {
      return;
    }
    setAttemptAudioLoading(prev => ({ ...prev, [attemptId]: true }));
    setAttemptAudioError(prev => {
      const next = { ...prev };
      delete next[attemptId];
      return next;
    });
    try {
      const response = await authenticatedRequest(headers =>
        fetch(
          `${apiUrl}/api/lead-engagement/agent-attempt/${attemptId}/audio`,
          { headers },
        ),
      );
      if (!response.ok) {
        const message =
          response.status === 404
            ? 'No recording available'
            : `Failed to load audio (${response.status})`;
        setAttemptAudioError(prev => ({ ...prev, [attemptId]: message }));
        return;
      }
      const blob = await response.blob();
      setAttemptAudioUrls(prev => ({
        ...prev,
        [attemptId]: URL.createObjectURL(blob),
      }));
    } catch (err) {
      console.error('Error fetching agent attempt audio:', err);
      setAttemptAudioError(prev => ({
        ...prev,
        [attemptId]: 'Failed to load audio',
      }));
    } finally {
      setAttemptAudioLoading(prev => {
        const next = { ...prev };
        delete next[attemptId];
        return next;
      });
    }
  };

  const fetchRoutingData = async (engagement: LeadEngagement) => {
    setRoutingLoading(true);
    try {
      const response = await authenticatedRequest(headers =>
        axios.get(`${apiUrl}/api/lead-engagement/${engagement.id}/routing`, {
          headers,
        }),
      );
      setRoutingData(response.data);
    } catch (error) {
      console.error('Failed to fetch routing data:', error);
      setRoutingData([]);
    } finally {
      setRoutingLoading(false);
    }
  };

  const handleToggleActive = () => {
    if (!selectedEngagement) {
      return;
    }
    const engagementId = selectedEngagement.id;
    const nextActive = !selectedEngagement.is_active;
    void (async () => {
      setToggleActiveLoading(true);
      try {
        await authenticatedRequest(headers =>
          axios.patch(
            `${apiUrl}/api/lead-engagement/${engagementId}/toggle-active`,
            { toggle: nextActive },
            { headers },
          ),
        );
        setSelectedEngagement(prev =>
          prev ? { ...prev, is_active: nextActive } : null,
        );
      } catch (error) {
        console.error('Error toggling engagement:', error);
      } finally {
        setToggleActiveLoading(false);
      }
    })();
  };

  const handleReengage = async () => {
    if (!selectedEngagement) {
      return;
    }

    await authenticatedRequest(headers =>
      reengageLead(apiUrl, selectedEngagement.lead_id, headers),
    );
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    if (key === 'notes' && selectedEngagement) {
      // Always fetch notes when switching to notes tab to ensure freshness
      void showMoreInfo(selectedEngagement, 'notes');
    } else if (key === 'calls' && selectedEngagement) {
      // Always fetch calls when switching to calls tab to ensure freshness
      void showMoreInfo(selectedEngagement, 'calls');
    } else if (key === 'routing' && selectedEngagement) {
      void fetchRoutingData(selectedEngagement);
    }
  };

  const getLeadEngagementHref = (leadId: string | number) =>
    `/lead-engagements/${clientId}?leadId=${leadId}`;

  const handleMoreInfoClick =
    (record: LeadEngagement) => (e: React.MouseEvent<HTMLElement>) => {
      if (!e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        void showMoreInfo(record);
      }
    };

  // Shared by every relative-date column (Next Evaluation, Transfer Date, ...): renders '-' for a
  // missing date, otherwise the relative time with the exact zoned timestamp in a tooltip.
  // `overdueColor` is only ever set when the field also means "action due" (e.g. next_evaluation_at).
  const renderRelativeDate = (
    date: string | undefined,
    record: LeadEngagement,
    overdueColor?: string,
  ) => {
    if (!date) {
      return '-';
    }
    const timezone = getAgentTimezone(
      getFullAgent(record.lead?.agent),
      clientConfig,
    );
    const overdue = !!overdueColor && isOverdue(date);
    return (
      <Tooltip title={formatDateWithZone(date, timezone)}>
        <span
          style={overdue ? { color: overdueColor, fontWeight: 500 } : undefined}
        >
          {relativeFromNow(date)}
        </span>
      </Tooltip>
    );
  };

  const dateFieldSorter =
    (field: 'created_at' | 'next_evaluation_at' | 'transfer_date') =>
    (a: LeadEngagement, b: LeadEngagement) => {
      const aTime = a[field] ? new Date(a[field] as string).getTime() : 0;
      const bTime = b[field] ? new Date(b[field] as string).getTime() : 0;
      return aTime - bTime;
    };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      filteredValue: filters.name || null,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            onChange={e =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            placeholder="Search name"
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            value={selectedKeys[0]}
          />
          <Space>
            <Button
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
              type="primary"
            >
              Search
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      render: (_: any, record: LeadEngagement) => {
        // Rejected rows have no engagement to open; their name opens the ledger popup instead.
        const isRejected = record.lead_engagement_id === null;
        return (
          <Space size={4}>
            <Button
              href={
                isRejected ? undefined : getLeadEngagementHref(record.lead_id)
              }
              onClick={
                isRejected
                  ? () => setRejectedLead(record)
                  : handleMoreInfoClick(record)
              }
              style={{ padding: 0 }}
              type="link"
            >
              <strong>
                {record.lead
                  ? `${record.lead.first_name} ${record.lead.last_name}`
                  : 'Unknown'}
              </strong>
            </Button>
            {!isRejected && (
              <Tooltip title="Open as a full page">
                <Link
                  style={{ color: '#8c8c8c' }}
                  to={`/lead-engagements/${clientId}/${record.lead_id}`}
                >
                  <ExportOutlined />
                </Link>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Source ID',
      key: 'source_id',
      sorter: true,
      render: (_: any, record: LeadEngagement) => (
        <Typography.Text copyable>
          {record.lead?.source_id || '-'}
        </Typography.Text>
      ),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            onChange={e =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            placeholder="Search Source ID"
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            value={selectedKeys[0]}
          />
          <Space>
            <Button
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
              type="primary"
            >
              Search
            </Button>
            <Button
              onClick={() => clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
    },
    {
      title: 'Lead ID',
      key: 'lead_id',
      dataIndex: 'lead_id',
      sorter: true,
      width: 130,
      render: (id: string) => (
        <Typography.Text copyable={{ text: id }} style={{ fontSize: '12px' }}>
          <span style={{ fontFamily: 'monospace' }}>{id.slice(0, 8)}…</span>
        </Typography.Text>
      ),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            onChange={e =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            placeholder="Search Lead ID"
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            value={selectedKeys[0]}
          />
          <Space>
            <Button
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
              type="primary"
            >
              Search
            </Button>
            <Button
              onClick={() => clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
    },
    {
      title: 'Agent',
      key: 'agent',
      render: (_: any, record: LeadEngagement) =>
        record.lead?.agent ? (
          <Tag color="purple" style={{ fontSize: '13px', padding: '4px 12px' }}>
            {record.lead.agent.first_name} {record.lead.agent.last_name}
          </Tag>
        ) : (
          <Typography.Text type="secondary">No Agent</Typography.Text>
        ),
      onFilter: (value: any, record: LeadEngagement) => {
        const agentName = record.lead?.agent
          ? `${record.lead.agent.first_name} ${record.lead.agent.last_name}`
          : '';
        return agentName === value;
      },
    },
    {
      title: 'Type',
      dataIndex: 'lead_type',
      key: 'lead_type',
      render: (type: string) => (type ? <Tag color="blue">{type}</Tag> : '-'),
      onFilter: (value: any, record: LeadEngagement) =>
        record.lead_type === value,
    },
    {
      title: 'Stage',
      key: 'stage',
      render: (_: any, record: LeadEngagement) =>
        record.stage ? (
          <Space direction="vertical" size={2}>
            <Tag color={STAGE_COLORS[record.stage] || 'default'}>
              {record.stage.replace(/_/g, ' ')}
            </Tag>
            {record.outreach_channel && (
              <Typography.Text style={{ fontSize: '11px' }} type="secondary">
                {CHANNEL_LABELS[record.outreach_channel] ||
                  record.outreach_channel.replace(/_/g, ' ')}
              </Typography.Text>
            )}
          </Space>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Campaign',
      key: 'campaign_key',
      render: (_: unknown, record: LeadEngagement) =>
        record.campaign_key ? (
          <Tag color="geekblue">{record.campaign_key.replace(/_/g, ' ')}</Tag>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
      filters: campaignKeys.map(key => ({
        text: key.replace(/_/g, ' '),
        value: key,
      })),
      filterSearch: true,
      filteredValue: filters.campaign_key || null,
      // Sent to the server as `campaign_key`; also applied to the loaded page so the filter
      // works even where the backend doesn't honor the param yet.
      onFilter: (value: unknown, record: LeadEngagement) =>
        record.campaign_key === value,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value: any, record: LeadEngagement) =>
        record.is_active === value,
    },
    {
      title: 'Ledger State',
      key: 'ledger_state',
      render: (_: unknown, record: LeadEngagement) => {
        const state = record.ledger_state;
        return state ? <Tag color={eventColor(state)}>{state}</Tag> : '-';
      },
      filters: LEDGER_STATE_FILTERS,
      filterMultiple: false,
      // Controlled from `filters` state (the table is in controlled-filter mode via other columns);
      // server does the filtering, so no client-side onFilter. Lets Reset/clear propagate.
      filteredValue: filters.ledger_state || null,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string, record: LeadEngagement) =>
        renderRelativeDate(date, record),
      sorter: dateFieldSorter('created_at'),
    },
    {
      title: 'Next Evaluation',
      dataIndex: 'next_evaluation_at',
      key: 'next_evaluation_at',
      render: (date: string | undefined, record: LeadEngagement) =>
        renderRelativeDate(date, record, '#d46b08'),
      sorter: dateFieldSorter('next_evaluation_at'),
    },
    {
      title: 'Transfer Date',
      dataIndex: 'transfer_date',
      key: 'transfer_date',
      render: (date: string | undefined, record: LeadEngagement) =>
        renderRelativeDate(date, record),
      sorter: dateFieldSorter('transfer_date'),
    },
    {
      title: 'Lead Replies',
      dataIndex: 'message_count',
      key: 'message_count',
      render: (count: number) => (
        <Tag color={count > 0 ? 'green' : 'default'}>{count || 0}</Tag>
      ),
      sorter: (a: LeadEngagement, b: LeadEngagement) =>
        (a.message_count || 0) - (b.message_count || 0),
    },
    {
      title: 'AI Sent',
      dataIndex: 'ai_message_count',
      key: 'ai_message_count',
      render: (count: number) => (
        <Tag color={count > 0 ? 'blue' : 'default'}>{count || 0}</Tag>
      ),
      sorter: (a: LeadEngagement, b: LeadEngagement) =>
        (a.ai_message_count || 0) - (b.ai_message_count || 0),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <ClientNavigation
        activeTab="leads" // Reusing leads tab for now or could be a new one
        clientId={clientId}
        clientName={clientConfig?.friendly_name}
        crmLink={clientConfig?.referral_url}
        crmType={clientConfig?.crm_type as CrmType}
        numericClientId={parseInt(clientId || '0')}
      />

      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Lead Engagements
        </Title>
        <Space>
          <Typography.Text strong>Show</Typography.Text>
          <Select<ConnectionFilter>
            onChange={handleConnectionFilterChange}
            options={[
              { value: 'all', label: 'All Accepted Leads' },
              { value: 'is_connected', label: 'Only Connected' },
              { value: 'is_three_way', label: 'Only 3-Way' },
              { value: 'rejected', label: 'Rejected' },
            ]}
            style={{ width: 180 }}
            value={connectionFilterValue}
          />
        </Space>
      </div>

      <Tabs activeKey={pageTab} onChange={setPageTab}>
        <Tabs.TabPane key="engagements" tab="Engagements">
          <Table
            columns={columns}
            dataSource={engagements}
            loading={loading}
            onChange={handleTableChange}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
            }}
            rowClassName={record => {
              if (record.is_three_way) {
                return 'three-way-row';
              }
              return record.is_connected ? 'connected-row' : '';
            }}
            rowKey="id"
          />
        </Tabs.TabPane>
        <Tabs.TabPane key="metrics" tab="Metrics">
          <div style={{ minHeight: '450px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 24,
              }}
            >
              <Typography.Title level={2} style={{ margin: 0 }}>
                Core System Health
              </Typography.Title>
              <Space align="center" size="middle">
                {systemHealthUpdatedAt && (
                  <Typography.Text type="secondary">
                    Updated at {systemHealthUpdatedAt.toLocaleString()}
                  </Typography.Text>
                )}
                <Button
                  icon={<ReloadOutlined />}
                  loading={systemHealthLoading}
                  onClick={() => void fetchSystemHealth()}
                >
                  Refresh
                </Button>
              </Space>
            </div>
            {systemHealthLoading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 280,
                }}
              >
                <Spin size="large" />
              </div>
            ) : systemHealthMetrics ? (
              <Row gutter={[16, 16]}>
                <Col md={8} sm={24} xs={24}>
                  <Tooltip title="Percent of leads that have tried to get connected to an agent and were successfully connected.">
                    <Card>
                      <Statistic
                        title="New leads reached out"
                        value={systemHealthMetrics.new_leads_engaged}
                      />
                      <Typography.Text type="secondary">
                        # of new leads that have been reached out in the past
                        week.
                      </Typography.Text>
                    </Card>
                  </Tooltip>
                </Col>
                <Col md={8} sm={24} xs={24}>
                  <Tooltip title="Amount of leads that have been reached out throughout the past week.">
                    <Card>
                      <Statistic
                        title="Leads engaged"
                        value={systemHealthMetrics.leads_engaged}
                      />
                      <Typography.Text type="secondary">
                        Engagement rate:{' '}
                        {(systemHealthMetrics.engagement_rate <= 1
                          ? systemHealthMetrics.engagement_rate * 100
                          : systemHealthMetrics.engagement_rate
                        ).toFixed(1)}
                        %
                      </Typography.Text>
                    </Card>
                  </Tooltip>
                </Col>
                <Col md={8} sm={24} xs={24}>
                  <Tooltip title="Percent of calls that agents have picked up.">
                    <Card>
                      <Statistic
                        precision={1}
                        suffix="%"
                        title="Agent connection rate"
                        value={
                          systemHealthMetrics.agent_connection_rate <= 1
                            ? systemHealthMetrics.agent_connection_rate * 100
                            : systemHealthMetrics.agent_connection_rate
                        }
                      />
                      <Typography.Text type="secondary">
                        % of calls where agent picked up
                      </Typography.Text>
                    </Card>
                  </Tooltip>
                </Col>
                <Col md={8} sm={24} xs={24}>
                  <Tooltip title="Percent of leads that have tried to get connected to an agent and were successfully connected.">
                    <Card>
                      <Statistic
                        precision={1}
                        suffix="%"
                        title="Leads connected to agent"
                        value={
                          systemHealthMetrics.leads_connected_rate <= 1
                            ? systemHealthMetrics.leads_connected_rate * 100
                            : systemHealthMetrics.leads_connected_rate
                        }
                      />
                      <Typography.Text type="secondary">
                        % of leads connected to an agent
                      </Typography.Text>
                    </Card>
                  </Tooltip>
                </Col>
              </Row>
            ) : (
              <Tooltip title="Tooltip placeholder – System health">
                <Card>
                  <Typography.Text type="secondary">
                    System health metrics will appear here once the backend
                    endpoint is available.
                  </Typography.Text>
                </Card>
              </Tooltip>
            )}
          </div>
        </Tabs.TabPane>
      </Tabs>

      <Modal
        centered
        footer={null}
        onCancel={() => setRejectedLead(null)}
        open={rejectedLead !== null}
        styles={{
          body: { maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' },
          content: { borderRadius: '16px' },
        }}
        title={
          rejectedLead?.lead
            ? `${rejectedLead.lead.first_name} ${rejectedLead.lead.last_name}`
            : 'Rejected Lead'
        }
        width={900}
      >
        {rejectedLead && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Status">
                <Tag color="purple">Rejected ({rejectedLead.ledger_state})</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Rejected At">
                {new Date(rejectedLead.created_at).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Reason for Rejection" span={2}>
                {rejectedLead.ledger_reason || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {rejectedLead.phone_number || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {rejectedLead.lead?.email || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Source ID">
                {rejectedLead.lead?.source_id || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Lead ID">
                <Typography.Text copyable>
                  {rejectedLead.lead_id}
                </Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="Source">
                {rejectedLead.lead?.source || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Stage">
                {rejectedLead.lead?.stage || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Pond">
                {rejectedLead.lead?.pond_name ||
                  rejectedLead.lead?.pond_id ||
                  '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Agent">
                {rejectedLead.lead?.agent
                  ? `${rejectedLead.lead.agent.first_name} ${rejectedLead.lead.agent.last_name}`
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Tags" span={2}>
                {rejectedLead.lead?.tags?.length
                  ? rejectedLead.lead.tags.map(tag => (
                      <Tag key={tag}>{tag}</Tag>
                    ))
                  : '-'}
              </Descriptions.Item>
            </Descriptions>
            <LeadEngagementLedger
              apiUrl={apiUrl}
              leadID={rejectedLead.lead_id}
            />
          </Space>
        )}
      </Modal>

      <Modal
        centered
        footer={null}
        maskClosable={true}
        onCancel={handleInfoModalCancel}
        open={isInfoModalVisible}
        style={{ maxWidth: 'calc(100vw - 16px)' }}
        styles={{
          body: { maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' },
          content: { borderRadius: '16px' },
        }}
        title={`${selectedEngagement?.lead ? `${selectedEngagement.lead.first_name} ${selectedEngagement.lead.last_name}` : 'Lead Information'}`}
        width={1200}
      >
        {selectedEngagement && (
          <div className="engagement-overview-modal-body">
            <EngagementSummary
              engagement={selectedEngagement}
              timezone={engagementTimezone}
            />
            <Tabs activeKey={activeTab} onChange={handleTabChange}>
              <Tabs.TabPane key="timeline" tab="Overview">
                <div style={{ minHeight: '450px' }}>
                  <EngagementTimeline
                    accessToken={user?.accessToken || ''}
                    agentId={selectedEngagement.lead?.agent?.id}
                    apiUrl={apiUrl}
                    campaignKey={selectedEngagement.campaign_key}
                    clientId={clientId}
                    crmLink={getCrmLink(
                      clientConfig,
                      selectedEngagement.lead?.source_id,
                    )}
                    engagementId={selectedEngagement.id}
                    leadId={selectedEngagement.lead_id}
                    sourceId={selectedEngagement.lead?.source_id}
                    timezone={engagementTimezone}
                  />
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane key="messages" tab="Messages">
                <div
                  style={{
                    minHeight: '450px',
                    background: '#fafafa',
                    borderRadius: '12px',
                    padding: '16px',
                  }}
                >
                  <LeadConversation
                    accessToken={user?.accessToken || ''}
                    apiUrl={apiUrl}
                    engagementId={selectedEngagement.id}
                    targetMessageId={initialMessageId}
                    timezone={engagementTimezone}
                  />
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane key="ledger" tab="Engagement Ledger">
                <div style={{ minHeight: '450px' }}>
                  <LeadEngagementLedger
                    apiUrl={apiUrl}
                    leadID={selectedEngagement.lead_id}
                    onStateChange={(leadID, state) => {
                      setEngagements(prev =>
                        prev.map(e =>
                          e.lead_id === leadID
                            ? { ...e, ledger_state: state }
                            : e,
                        ),
                      );
                    }}
                  />
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane key="notes" tab="Notes">
                <div style={{ minHeight: '450px' }}>
                  {notesLoading ? (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '60px',
                        background: '#fafafa',
                        borderRadius: '12px',
                      }}
                    >
                      <Spin size="large" />
                    </div>
                  ) : notes.length === 0 ? (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '60px',
                        color: '#999',
                        background: '#fafafa',
                        borderRadius: '12px',
                      }}
                    >
                      No notes found for this lead
                    </div>
                  ) : (
                    <List
                      dataSource={notes}
                      renderItem={(note: any) => (
                        <List.Item
                          style={{
                            borderBottom: '1px solid #f0f0f0',
                            padding: '20px',
                            background: 'white',
                            borderRadius: '12px',
                            marginBottom: '12px',
                            border: '1px solid #f0f0f0',
                          }}
                        >
                          <div style={{ width: '100%' }}>
                            {note.header && (
                              <Typography.Title
                                level={5}
                                style={{ marginBottom: 12, marginTop: 0 }}
                              >
                                {note.header}
                              </Typography.Title>
                            )}
                            <div
                              dangerouslySetInnerHTML={{
                                __html: sanitizeHTML(note.body),
                              }}
                              style={{
                                marginBottom: 12,
                                lineHeight: '1.6',
                                color: '#262626',
                              }}
                            />
                            <div
                              style={{
                                display: 'flex',
                                gap: '16px',
                                flexWrap: 'wrap',
                              }}
                            >
                              {note.created_at && (
                                <Typography.Text
                                  style={{ fontSize: '13px' }}
                                  type="secondary"
                                >
                                  <Tooltip title={engagementTimezone}>
                                    <span>
                                      {formatDateWithZone(
                                        note.created_at,
                                        engagementTimezone,
                                      )}
                                    </span>
                                  </Tooltip>
                                </Typography.Text>
                              )}
                              {note.created_by && (
                                <Typography.Text
                                  style={{ fontSize: '13px' }}
                                  type="secondary"
                                >
                                  By: {note.created_by}
                                </Typography.Text>
                              )}
                            </div>
                          </div>
                        </List.Item>
                      )}
                    />
                  )}
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane key="calls" tab="Calls">
                <div style={{ minHeight: '450px' }}>
                  {callsLoading ? (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '60px',
                        background: '#fafafa',
                        borderRadius: '12px',
                      }}
                    >
                      <Spin size="large" />
                    </div>
                  ) : calls.length === 0 ? (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '60px',
                        color: '#999',
                        background: '#fafafa',
                        borderRadius: '12px',
                      }}
                    >
                      No calls found for this lead
                    </div>
                  ) : (
                    <List
                      dataSource={calls}
                      renderItem={(call: any) => (
                        <List.Item
                          style={{
                            borderBottom: '1px solid #f0f0f0',
                            padding: '20px',
                            background: 'white',
                            borderRadius: '12px',
                            marginBottom: '12px',
                            border: '1px solid #f0f0f0',
                          }}
                        >
                          <div style={{ width: '100%' }}>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 12,
                              }}
                            >
                              <Typography.Title
                                level={5}
                                style={{
                                  margin: 0,
                                  textTransform: 'capitalize',
                                }}
                              >
                                {call.post_call_analysis_result?.call_result?.replace(
                                  '_',
                                  ' ',
                                ) ||
                                  (call.is_voicemail
                                    ? 'Voicemail'
                                    : 'Unknown Status')}
                              </Typography.Title>
                              {call.duration && (
                                <Tag color="blue">
                                  {Math.floor(call.duration / 60)}m{' '}
                                  {call.duration % 60}s
                                </Tag>
                              )}
                            </div>

                            {(call.recording_url ||
                              call.external_conversation_id) && (
                              <div
                                style={{
                                  marginBottom: '12px',
                                  marginTop: '8px',
                                }}
                              >
                                {audioUrls[call.id] ? (
                                  <audio
                                    controls
                                    preload="auto"
                                    src={audioUrls[call.id]}
                                    style={{ width: '100%', height: '36px' }}
                                  />
                                ) : (
                                  'No audio available'
                                )}
                              </div>
                            )}
                            <div>
                              {call.transcript && (
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: call.transcript
                                      .replace(/\bagent:/gi, '<b>AI:</b>')
                                      .replace(/\buser:/gi, '<b>LEAD:</b>')
                                      .replace(
                                        /\[TOOL_RESULT\]/gi,
                                        '<b>TOOL CALLED:</b>',
                                      )
                                      .replace(/\n/g, '<br />'),
                                  }}
                                />
                              )}
                            </div>

                            <div
                              style={{
                                display: 'flex',
                                gap: '16px',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                              }}
                            >
                              {call.created_at && (
                                <Typography.Text
                                  style={{ fontSize: '13px' }}
                                  type="secondary"
                                >
                                  📞{' '}
                                  <Tooltip title={engagementTimezone}>
                                    <span>
                                      {formatDateWithZone(
                                        call.created_at,
                                        engagementTimezone,
                                      )}
                                    </span>
                                  </Tooltip>
                                </Typography.Text>
                              )}
                              {call.call_type && <Tag>{call.call_type}</Tag>}
                              {call.external_conversation_id && (
                                <Button
                                  href={`https://elevenlabs.io/app/agents/history/${call.external_conversation_id}`}
                                  icon={<ExportOutlined />}
                                  rel="noopener noreferrer"
                                  size="small"
                                  style={{ padding: 0, height: 'auto' }}
                                  target="_blank"
                                  type="link"
                                >
                                  ElevenLabs
                                </Button>
                              )}
                            </div>
                          </div>
                        </List.Item>
                      )}
                    />
                  )}
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane key="cadence" tab="Engine Decisions">
                <div style={{ minHeight: '450px' }}>
                  <CadenceTimeline
                    accessToken={user?.accessToken || ''}
                    apiUrl={apiUrl}
                    engagementId={selectedEngagement.id}
                    enrollment={selectedEngagement}
                    timezone={engagementTimezone}
                  />
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane key="routing" tab="Routing">
                <div style={{ minHeight: '450px' }}>
                  {routingLoading ? (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '60px',
                        background: '#fafafa',
                        borderRadius: '12px',
                      }}
                    >
                      <Spin size="large" />
                    </div>
                  ) : routingData.length === 0 ? (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '60px',
                        color: '#999',
                        background: '#fafafa',
                        borderRadius: '12px',
                      }}
                    >
                      No routing data found for this lead
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                      }}
                    >
                      {routingData.map((call: any) => {
                        const routingDuration =
                          call.routing_started_at && call.routing_completed_at
                            ? Math.round(
                                (new Date(call.routing_completed_at).getTime() -
                                  new Date(call.routing_started_at).getTime()) /
                                  1000,
                              )
                            : null;

                        return (
                          <div
                            key={call.call_id}
                            style={{
                              background: 'white',
                              borderRadius: '12px',
                              padding: '20px',
                              border: '1px solid #f0f0f0',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                            }}
                          >
                            {/* Call header */}
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 16,
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                }}
                              >
                                <PhoneOutlined
                                  style={{ fontSize: 16, color: '#1890ff' }}
                                />
                                <Typography.Text strong>
                                  {call.call_source
                                    ? call.call_source
                                        .replace(/_/g, ' ')
                                        .replace(/\b\w/g, (c: string) =>
                                          c.toUpperCase(),
                                        )
                                    : 'Call'}
                                </Typography.Text>
                                {call.call_outcome && (
                                  <Tag
                                    color={
                                      call.call_outcome === 'completed'
                                        ? 'green'
                                        : call.call_outcome === 'no_answer'
                                          ? 'orange'
                                          : call.call_outcome === 'busy'
                                            ? 'red'
                                            : 'default'
                                    }
                                    style={{ borderRadius: '6px' }}
                                  >
                                    {call.call_outcome.replace(/_/g, ' ')}
                                  </Tag>
                                )}
                                {routingDuration !== null && (
                                  <Tag
                                    color="blue"
                                    style={{ borderRadius: '6px' }}
                                  >
                                    <ClockCircleOutlined /> Routed in{' '}
                                    {routingDuration}s
                                  </Tag>
                                )}
                                {call.external_conversation_id && (
                                  <Button
                                    href={`https://elevenlabs.io/app/agents/history/${call.external_conversation_id}`}
                                    icon={<ExportOutlined />}
                                    rel="noopener noreferrer"
                                    size="small"
                                    style={{
                                      padding: 0,
                                      height: 'auto',
                                      marginLeft: 4,
                                    }}
                                    target="_blank"
                                    type="link"
                                  >
                                    ElevenLabs
                                  </Button>
                                )}
                              </div>
                              <Typography.Text
                                style={{ fontSize: '13px' }}
                                type="secondary"
                              >
                                <Tooltip title={engagementTimezone}>
                                  <span>
                                    {call.created_at
                                      ? formatDateWithZone(
                                          call.created_at,
                                          engagementTimezone,
                                        )
                                      : ''}
                                  </span>
                                </Tooltip>
                              </Typography.Text>
                            </div>

                            {/* Routed agent summary */}
                            {call.routed_agent && (
                              <div
                                style={{
                                  background: '#f6ffed',
                                  border: '1px solid #b7eb8f',
                                  borderRadius: '8px',
                                  padding: '10px 14px',
                                  marginBottom: 16,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                }}
                              >
                                <CheckCircleOutlined
                                  style={{ color: '#52c41a', fontSize: 16 }}
                                />
                                <Typography.Text>
                                  Connected to{' '}
                                  <strong>
                                    {call.routed_agent.first_name}{' '}
                                    {call.routed_agent.last_name}
                                  </strong>
                                </Typography.Text>
                              </div>
                            )}

                            {/* Call recording */}
                            {call.routed_agent &&
                              (call.recording_url ||
                                call.external_conversation_id) && (
                                <div style={{ marginBottom: 16 }}>
                                  <Typography.Text
                                    style={{
                                      fontSize: '12px',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                      fontWeight: 500,
                                      color: '#8c8c8c',
                                    }}
                                  >
                                    Call Recording
                                  </Typography.Text>
                                  <audio
                                    controls
                                    preload="none"
                                    src={
                                      call.external_conversation_id
                                        ? `${apiUrl}/api/lead-engagement/call/${call.call_id}/audio`
                                        : call.recording_url
                                    }
                                    style={{
                                      width: '100%',
                                      height: '36px',
                                      marginTop: 6,
                                    }}
                                  />
                                </div>
                              )}

                            {/* Agent attempts timeline */}
                            {call.attempts.length > 0 ? (
                              <Timeline
                                items={call.attempts.map((attempt: any) => {
                                  const isAnswered = !!attempt.answered_at;
                                  const isRouted = attempt.was_routed;

                                  let color: string = 'gray';
                                  let dotIcon = undefined;
                                  if (isRouted) {
                                    color = 'green';
                                    dotIcon = (
                                      <CheckCircleOutlined
                                        style={{ fontSize: '14px' }}
                                      />
                                    );
                                  } else if (isAnswered) {
                                    color = 'blue';
                                  } else if (
                                    attempt.call_status === 'no-answer'
                                  ) {
                                    color = 'orange';
                                  } else if (
                                    attempt.call_status === 'busy' ||
                                    attempt.call_status === 'failed'
                                  ) {
                                    color = 'red';
                                    dotIcon = (
                                      <CloseCircleOutlined
                                        style={{ fontSize: '14px' }}
                                      />
                                    );
                                  }

                                  const statusLabel = attempt.call_status
                                    ? attempt.call_status.replace(/-|_/g, ' ')
                                    : 'pending';
                                  const attemptAudioUrl =
                                    attemptAudioUrls[attempt.id];
                                  const attemptIsLoadingAudio =
                                    !!attemptAudioLoading[attempt.id];
                                  const attemptAudioErrorMsg =
                                    attemptAudioError[attempt.id];
                                  // Only offer audio when the agent actually picked up.
                                  const canPlayAudio =
                                    attempt.id && (isAnswered || isRouted);
                                  return {
                                    color,
                                    dot: dotIcon,
                                    children: (
                                      <div
                                        style={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: 6,
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                          }}
                                        >
                                          <div>
                                            <Typography.Text strong>
                                              {attempt.agent_name}
                                            </Typography.Text>
                                            <Typography.Text
                                              copyable={{
                                                text: attempt.agent_id,
                                              }}
                                              style={{
                                                fontSize: '11px',
                                                marginLeft: 6,
                                              }}
                                              type="secondary"
                                            >
                                              ({attempt.agent_id})
                                            </Typography.Text>
                                            <Tag
                                              color={
                                                isRouted
                                                  ? 'green'
                                                  : isAnswered
                                                    ? 'blue'
                                                    : attempt.call_status ===
                                                          'busy' ||
                                                        attempt.call_status ===
                                                          'failed'
                                                      ? 'red'
                                                      : 'default'
                                              }
                                              style={{
                                                marginLeft: 8,
                                                borderRadius: '6px',
                                                textTransform: 'capitalize',
                                              }}
                                            >
                                              {isRouted
                                                ? 'connected'
                                                : statusLabel}
                                            </Tag>
                                          </div>
                                          <Typography.Text
                                            style={{ fontSize: '12px' }}
                                            type="secondary"
                                          >
                                            {attempt.created_at
                                              ? formatDateWithZone(
                                                  attempt.created_at,
                                                  engagementTimezone,
                                                )
                                              : ''}
                                          </Typography.Text>
                                        </div>
                                        <div
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '16px',
                                            flexWrap: 'wrap',
                                          }}
                                        >
                                          {canPlayAudio && (
                                            <div
                                              style={{
                                                flex: attemptAudioUrl
                                                  ? '1 1 100%'
                                                  : '0 1 auto',
                                              }}
                                            >
                                              {attemptAudioUrl ? (
                                                <audio
                                                  controls
                                                  preload="none"
                                                  src={attemptAudioUrl}
                                                  style={{
                                                    width: '100%',
                                                    height: '32px',
                                                  }}
                                                />
                                              ) : attemptAudioErrorMsg ? (
                                                <Typography.Text
                                                  style={{ fontSize: '12px' }}
                                                  type="secondary"
                                                >
                                                  {attemptAudioErrorMsg}
                                                </Typography.Text>
                                              ) : (
                                                <Button
                                                  icon={<AudioOutlined />}
                                                  loading={
                                                    attemptIsLoadingAudio
                                                  }
                                                  onClick={() => {
                                                    void loadAttemptAudio(
                                                      attempt.id,
                                                    );
                                                  }}
                                                  size="small"
                                                  style={{ padding: 0 }}
                                                  type="link"
                                                >
                                                  Listen to agent call
                                                </Button>
                                              )}
                                            </div>
                                          )}
                                          {call.external_conversation_id && (
                                            <Button
                                              href={`https://elevenlabs.io/app/agents/history/${call.external_conversation_id}`}
                                              icon={<ExportOutlined />}
                                              rel="noopener noreferrer"
                                              size="small"
                                              style={{
                                                padding: 0,
                                                height: 'auto',
                                              }}
                                              target="_blank"
                                              type="link"
                                            >
                                              ElevenLabs
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    ),
                                  };
                                })}
                              />
                            ) : (
                              <Typography.Text type="secondary">
                                No agent attempts recorded
                              </Typography.Text>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane key="agent" tab="More Info">
                <div style={{ minHeight: '450px' }}>
                  <EngagementOverview
                    agent={selectedAgent}
                    clientConfig={clientConfig}
                    engagement={selectedEngagement}
                    onReengage={handleReengage}
                    onToggleActive={handleToggleActive}
                    timezone={engagementTimezone}
                    toggleLoading={toggleActiveLoading}
                  />
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane key="crm" tab="CRM">
                <div style={{ minHeight: '450px' }}>
                  {selectedEngagement?.lead?.source_id &&
                  clientConfig?.crm_link ? (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '24px',
                        padding: '60px',
                        background: '#fafafa',
                        borderRadius: '12px',
                        textAlign: 'center',
                      }}
                    >
                      <div style={{ fontSize: '48px' }}>🔗</div>
                      <Typography.Title level={3} style={{ margin: 0 }}>
                        CRM Integration
                      </Typography.Title>
                      <Typography.Text
                        style={{ fontSize: '16px', color: '#666' }}
                      >
                        View this lead in your CRM system
                      </Typography.Text>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px',
                          alignItems: 'center',
                        }}
                      >
                        <Typography.Text strong>
                          Lead ID: {selectedEngagement.lead?.source_id}
                        </Typography.Text>
                        <Button
                          href={
                            getCrmLink(
                              clientConfig,
                              selectedEngagement.lead?.source_id,
                            ) || undefined
                          }
                          icon={<ExportOutlined />}
                          rel="noopener noreferrer"
                          size="large"
                          target="_blank"
                          type="primary"
                        >
                          Open in CRM
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '16px',
                        padding: '60px',
                        background: '#fafafa',
                        borderRadius: '12px',
                        textAlign: 'center',
                        color: '#999',
                      }}
                    >
                      <div style={{ fontSize: '48px' }}>🚫</div>
                      <Typography.Title
                        level={4}
                        style={{ margin: 0, color: '#999' }}
                      >
                        CRM Not Available
                      </Typography.Title>
                      <Typography.Text>
                        {!selectedEngagement?.lead?.source_id
                          ? 'This lead does not have a source ID for CRM integration.'
                          : 'CRM integration is not configured for this client.'}
                      </Typography.Text>
                    </div>
                  )}
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane key="facts" tab="Lead Facts">
                <div style={{ minHeight: '450px' }}>
                  {selectedEngagement?.lead_id && (
                    <LeadFacts
                      apiUrl={apiUrl}
                      leadId={selectedEngagement.lead_id}
                      timezone={engagementTimezone}
                    />
                  )}
                </div>
              </Tabs.TabPane>
            </Tabs>
          </div>
        )}
      </Modal>

      <Modal
        centered
        footer={null}
        maskClosable={true}
        onCancel={handleNoteDetailModalCancel}
        open={isNoteDetailModalVisible}
        styles={{
          content: { borderRadius: '16px' },
        }}
        title={selectedNote?.header || 'Note Details'}
        width={900}
      >
        {selectedNote && (
          <div style={{ padding: '8px 0' }}>
            <div
              dangerouslySetInnerHTML={{
                __html: sanitizeHTML(selectedNote.body),
              }}
              style={{
                marginBottom: 24,
                lineHeight: '1.8',
                color: '#262626',
                fontSize: '15px',
              }}
            />

            <div
              style={{
                borderTop: '1px solid #f0f0f0',
                paddingTop: 16,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
              }}
            >
              {selectedNote.created_by && (
                <div>
                  <Typography.Text
                    strong
                    style={{ display: 'block', marginBottom: 4 }}
                  >
                    Created By
                  </Typography.Text>
                  <Typography.Text>{selectedNote.created_by}</Typography.Text>
                </div>
              )}
              {selectedNote.agent_name && (
                <div>
                  <Typography.Text
                    strong
                    style={{ display: 'block', marginBottom: 4 }}
                  >
                    Agent
                  </Typography.Text>
                  <Typography.Text>{selectedNote.agent_name}</Typography.Text>
                </div>
              )}
              {selectedNote.action && (
                <div>
                  <Typography.Text
                    strong
                    style={{ display: 'block', marginBottom: 4 }}
                  >
                    Action
                  </Typography.Text>
                  <Typography.Text>{selectedNote.action}</Typography.Text>
                </div>
              )}
              {selectedNote.created_at && (
                <div>
                  <Typography.Text
                    strong
                    style={{ display: 'block', marginBottom: 4 }}
                  >
                    Created
                  </Typography.Text>
                  <Typography.Text>
                    <Tooltip title={engagementTimezone}>
                      <span>
                        {formatDateWithZone(
                          selectedNote.created_at,
                          engagementTimezone,
                        )}
                      </span>
                    </Tooltip>
                  </Typography.Text>
                </div>
              )}
              {selectedNote.updated_at && (
                <div>
                  <Typography.Text
                    strong
                    style={{ display: 'block', marginBottom: 4 }}
                  >
                    Updated
                  </Typography.Text>
                  <Typography.Text>
                    <Tooltip title={engagementTimezone}>
                      <span>
                        {formatDateWithZone(
                          selectedNote.updated_at,
                          engagementTimezone,
                        )}
                      </span>
                    </Tooltip>
                  </Typography.Text>
                </div>
              )}
              {selectedNote.note_type && (
                <div>
                  <Typography.Text
                    strong
                    style={{ display: 'block', marginBottom: 4 }}
                  >
                    Type
                  </Typography.Text>
                  <Tag color="blue">{selectedNote.note_type}</Tag>
                </div>
              )}
              {selectedNote.status && (
                <div>
                  <Typography.Text
                    strong
                    style={{ display: 'block', marginBottom: 4 }}
                  >
                    Status
                  </Typography.Text>
                  <Tag
                    color={
                      selectedNote.status === 'active' ? 'green' : 'default'
                    }
                  >
                    {selectedNote.status}
                  </Tag>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
